import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useT } from "@/lib/LanguageContext";
import * as crmApi from "../../api/crmApi";
import {
  PATHWAY_PROGRESS_UNIT_KEYS,
  PATHWAY_PROGRESS_UNITS,
  PATHWAY_TRIGGER_DEFAULT_PARAMS,
  PATHWAY_TRIGGERS,
  TRIGGER_TYPES,
  TRIGGER_KEYS,
  TRIGGER_NO_PARAM,
} from "../../contract";
import CrmSelect from "../../ui/CrmSelect";
import CrmInput from "../../ui/CrmInput";
import { crmHref } from "../../paths";
import { LABEL } from "../../ui/classes";
import { CRM } from "../../../../constants/testIds/crm";

const PARAM_FIELD = {
  no_activity: { key: "days", type: "number", min: 1, labelKey: "seq.triggerParamDays" },
  pack_balance_hits: { key: "n", type: "number", min: 0, labelKey: "seq.triggerParamN" },
  tag_added: { key: "tag", type: "text", labelKey: "seq.triggerParamTag" },
  nth_session_completed: { key: "n", type: "number", min: 1, labelKey: "seq.triggerParamN" },
};

/**
 * "Trigger" dropdown (spec Sequence editor): New client, Booking exists, Booking
 * completed, Purchase completed, No activity N days (N editable), Pack balance hits N,
 * Tag added, Nth session completed, Review moment. A SYSTEM sequence's trigger is fixed
 * (409 `SYSTEM_TRIGGER_LOCKED` server-side) — disabled here instead of racing it.
 * "Review moment" has no number of its own: it fires at the practice's
 * `settings.crm.review_threshold`, stated here with a link to where it is changed.
 *
 * The two PATHWAY triggers (pathways-api-contract §10.8: "Pathway step/level completed",
 * "Pathway completed") take a pathway picker instead of the single field: "Any pathway"
 * (`pathway_id: null`) or one of the practice's pathways, and for progress the unit
 * (a step or a level). Choosing one starts from PATHWAY_TRIGGER_DEFAULT_PARAMS, so the
 * params the server receives are always complete.
 */
export default function TriggerSelect({ trigger, onChange, locked, reviewThreshold }) {
  const t = useT("crm");
  const field = PARAM_FIELD[trigger?.type];
  const pathwayTrigger = PATHWAY_TRIGGERS.includes(trigger?.type);

  const setType = (type) => onChange({ type, params: { ...(PATHWAY_TRIGGER_DEFAULT_PARAMS[type] || {}) } });
  const setParam = (value) => onChange({ ...trigger, params: { ...trigger.params, [field.key]: value } });

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <label htmlFor="crm-seq-trigger" className={LABEL}>{t("seq.triggerLabel")}</label>
        <CrmSelect id="crm-seq-trigger" value={trigger?.type || ""} disabled={locked}
          onChange={(e) => setType(e.target.value)} data-testid={CRM.seqTrigger}
          options={TRIGGER_TYPES.map((ty) => ({ value: ty, label: t(TRIGGER_KEYS[ty]) }))} />
      </div>
      {field && !TRIGGER_NO_PARAM.includes(trigger?.type) && (
        <div>
          <label htmlFor="crm-seq-trigger-param" className={LABEL}>{t(field.labelKey)}</label>
          <CrmInput id="crm-seq-trigger-param" type={field.type} min={field.min} disabled={locked}
            value={trigger?.params?.[field.key] ?? ""} onChange={(e) => setParam(field.type === "number" ? Number(e.target.value) : e.target.value)}
            data-testid={CRM.seqTriggerParam} className="w-28" />
        </div>
      )}
      {pathwayTrigger && (
        <PathwayParams trigger={trigger} locked={locked}
          onParams={(params) => onChange({ ...trigger, params: { ...trigger.params, ...params } })} />
      )}
      {trigger?.type === "review_moment" && reviewThreshold && (
        <p className="text-xs text-eh-muted basis-full" data-testid={CRM.seqReviewThreshold}>
          {t("seq.reviewThresholdHint", { n: reviewThreshold })}{" "}
          <Link to={crmHref("/settings")} className="underline text-eh-ink">{t("seq.reviewThresholdLink")}</Link>
        </p>
      )}
      {locked && <p className="text-xs text-eh-muted basis-full">{t("seq.triggerLocked")}</p>}
    </div>
  );
}

/**
 * The pathway picker (and, for progress, the unit). The practice's pathways are read once
 * the editor shows a pathway trigger; a stored `pathway_id` the list no longer holds is
 * kept as a marked option rather than silently turned into "Any pathway".
 */
function PathwayParams({ trigger, locked, onParams }) {
  const t = useT("crm");
  const [pathways, setPathways] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    crmApi.listPathways()
      .then((rows) => { if (alive) setPathways(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, []);

  const pathwayId = trigger.params?.pathway_id || "";
  const known = (pathways || []).some((p) => p.id === pathwayId);
  const options = [
    { value: "", label: t("seq.triggerPathwayAny") },
    ...(pathwayId && pathways && !known ? [{ value: pathwayId, label: t("seq.triggerPathwayUnknown") }] : []),
    ...(pathways || []).map((p) => ({ value: p.id, label: p.title })),
  ];

  return (
    <>
      <div>
        <label htmlFor="crm-seq-trigger-pathway" className={LABEL}>{t("seq.triggerPathwayLabel")}</label>
        <CrmSelect id="crm-seq-trigger-pathway" value={pathwayId} disabled={locked || pathways === null}
          onChange={(e) => onParams({ pathway_id: e.target.value || null })}
          data-testid={CRM.seqTriggerPathway} options={options} />
      </div>
      {trigger.type === "pathway_progress" && (
        <div>
          <label htmlFor="crm-seq-trigger-unit" className={LABEL}>{t("seq.triggerPathwayUnitLabel")}</label>
          <CrmSelect id="crm-seq-trigger-unit" value={trigger.params?.unit || "step"} disabled={locked}
            onChange={(e) => onParams({ unit: e.target.value })}
            data-testid={CRM.seqTriggerUnit}
            options={PATHWAY_PROGRESS_UNITS.map((u) => ({ value: u, label: t(PATHWAY_PROGRESS_UNIT_KEYS[u]) }))} />
        </div>
      )}
      {failed && (
        <p role="alert" className="text-xs text-rose-800 basis-full" data-testid={CRM.seqTriggerPathwayError}>
          {t("seq.triggerPathwayLoadFailed")}
        </p>
      )}
    </>
  );
}
